const storage = localStorage;

const SECRET_KEY = process.env.REACT_APP_STORAGE_SECRET || 'econ-fleet-local-dev-secret-key-12345';
const isProd = process.env.NODE_ENV === 'production';

// Dev override: set localStorage.__FORCE_ENCRYPTION__ = 'true' to exercise the
// full prod storage path (encryption + key obfuscation) while developing.
const forceEncryptionFlag = () => {
    try {
        return localStorage.__FORCE_ENCRYPTION__ === 'true';
    } catch (e) {
        return false;
    }
};

// Storage is "secured" (values encrypted + signed, keys obfuscated) in
// production, or in development when the override flag is set. Otherwise dev
// stores plain text so localStorage stays human-readable for debugging.
const isSecureMode = () => isProd || forceEncryptionFlag();

// Key mapping for physical key obfuscation in production
const KEY_MAP = {
    '_displayName_': '_x1d_',
    '_isReadOnlyUser_': '_x2r_',
    '_token_': '_x3t_',
    '_lastLogin_': '_x4l_',
    '_userRoles_': '_x5o_',
    '_login_info_updated_at_': '_x6u_',
    '_allowAccess_': '_x7a_',
    '_portal_setting_': '_x8p_',
    '__fileUploaderConfig__': '_x9c_',
    '_user_sign_': '_x10s_',
    '_signature_status_': '_x11g_',
    '_signature_notified_': '_x12n_',
    '_loggedIn_UI_version_': '_x13v_',
    '_twoFactorEnabled_': '_x14f_',
    '__last_activity__': '_x15a_'
};

const getPhysicalKey = (logicalKey) => {
    if (isSecureMode() && KEY_MAP[logicalKey]) {
        return KEY_MAP[logicalKey];
    }
    return logicalKey;
};

// Memory Cache to prevent repeated expensive decrypt/decode operations
let memoryCache = {};

// Hashing function for signature verification
const generateSignature = (data, secretKey) => {
    let hash = 0;
    const combined = data + secretKey;
    for (let i = 0; i < combined.length; i++) {
        const char = combined.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16);
};

// XOR Cipher
const xorCipher = (text, key) => {
    let result = '';
    for (let i = 0; i < text.length; i++) {
        result += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return result;
};

const encrypt = (plainText, secretKey) => {
    const utf8Text = unescape(encodeURIComponent(plainText));
    const xored = xorCipher(utf8Text, secretKey);
    return btoa(xored);
};

const decrypt = (cipherText, secretKey) => {
    const xored = atob(cipherText);
    const utf8Text = xorCipher(xored, secretKey);
    return decodeURIComponent(escape(utf8Text));
};

const encryptAndSign = (value) => {
    const encrypted = encrypt(value, SECRET_KEY);
    const signature = generateSignature(encrypted, SECRET_KEY);
    return `${encrypted}.${signature}`;
};

const decryptAndVerify = (storedValue) => {
    if (!storedValue) return null;
    const parts = storedValue.split('.');
    if (parts.length !== 2) {
        if (!isSecureMode()) {
            return storedValue; // Fallback to plaintext in development
        }
        console.warn('[SECURITY] Storage tamper detected: invalid format');
        return null;
    }
    const [encrypted, signature] = parts;
    const expectedSignature = generateSignature(encrypted, SECRET_KEY);
    if (signature !== expectedSignature) {
        if (!isSecureMode()) {
            return storedValue; // Fallback to plaintext in development
        }
        console.warn('[SECURITY] Storage tamper detected: signature mismatch');
        return null;
    }
    try {
        return decrypt(encrypted, SECRET_KEY);
    } catch (e) {
        if (!isSecureMode()) {
            return storedValue; // Fallback to plaintext in development
        }
        console.warn('[SECURITY] Storage decryption failed');
        return null;
    }
};

const setItem = (key, value) => {
    if (value === null || value === undefined) {
        removeItem(key);
        return;
    }
    const physicalKey = getPhysicalKey(key);
    const stringValue = typeof value === 'string' ? value : String(value);

    // Update memory cache
    memoryCache[key] = stringValue;

    // Encrypt + sign in secure mode; store plain text in dev for debugging.
    const secureValue = isSecureMode() ? encryptAndSign(stringValue) : stringValue;
    storage.setItem(physicalKey, secureValue);
};

const getItem = (key) => {
    if (memoryCache[key] !== undefined) {
        return memoryCache[key];
    }
    const physicalKey = getPhysicalKey(key);
    const secureValue = storage.getItem(physicalKey);

    const decrypted = decryptAndVerify(secureValue);
    if (decrypted !== null) {
        memoryCache[key] = decrypted;
    }
    return decrypted;
};

const removeItem = (key) => {
    delete memoryCache[key];
    const physicalKey = getPhysicalKey(key);
    storage.removeItem(physicalKey);
};

const clear = () => {
    memoryCache = {};
    storage.clear();
};

// Sync memory cache changes across multiple tabs
window.addEventListener('storage', (event) => {
    let logicalKey = event.key;
    if (isSecureMode()) {
        const found = Object.entries(KEY_MAP).find(([k, v]) => v === event.key);
        if (found) {
            logicalKey = found[0];
        }
    }

    if (event.newValue === null || event.newValue === undefined) {
        delete memoryCache[logicalKey];
    } else {
        const decrypted = decryptAndVerify(event.newValue);
        if (decrypted !== null) {
            memoryCache[logicalKey] = decrypted;
        }
    }
});

// Dev-only console helpers for inspecting the storage layer. Never exposed in
// production so the cipher primitives aren't handed to anyone at the console.
if (!isProd && typeof window !== 'undefined') {
    window.encrypt = (value) => encryptAndSign(String(value));
    window.decrypt = (value) => decryptAndVerify(value);
    window.forceEncryption = (enable = true) => {
        if (enable) {
            localStorage.__FORCE_ENCRYPTION__ = 'true';
        } else {
            delete localStorage.__FORCE_ENCRYPTION__;
        }
        return `Force encryption ${enable ? 'ON' : 'OFF'} — reload the page to apply it to existing reads.`;
    };
}

// Helper getter/setters modified to use wrapper methods
const getName = () => getItem('_displayName_');
const isReadOnlyUser = () => getItem('_isReadOnlyUser_') === 'true';
const getToken = () => getItem('_token_');
const getLastLogin = () => getItem('_lastLogin_');
const getUserRoles = () => (getItem('_userRoles_') || '').split(',');
const loginInfoUpdatedAt = () => getItem('_login_info_updated_at_');
const getAllowAcces = () => {
    const vl = getItem('_allowAccess_');
    if (vl) {
        try {
            return JSON.parse(vl);
        } catch (e) {
            return vl;
        }
    }
    return vl;
};

const getPortalSetting = () => {
    const setting = getItem('_portal_setting_');
    if (setting) {
        try {
            return JSON.parse(setting);
        } catch (e) {
            return setting;
        }
    }
    return setting;
};

const setPortalSetting = (setting) => {
    setItem('_portal_setting_', JSON.stringify(setting));
};

const saveFileUploaderConfig = (data) => {
    var obj = {};
    data.forEach((o) => (obj[o.Name] = o.Id));
    setItem('__fileUploaderConfig__', JSON.stringify(obj));
};

const getFileUploaderConfig = () => {
    try {
        return JSON.parse(getItem('__fileUploaderConfig__') || '{}');
    } catch (e) {
        return {};
    }
};

const getSignature = () => {
    return getItem('_user_sign_');
};

const setSignature = (sign) => {
    sign && setItem('_user_sign_', sign);
};

const getSignatureStatus = () => {
    return getItem('_signature_status_');
};

const setSignatureStatus = (ID) => {
    ID && setItem('_signature_status_', ID);
};

const getSignNotifiedStatus = () => {
    return getItem('_signature_notified_') || 0;
};

const setSignNotifiedStatus = (ID) => {
    ID && setItem('_signature_notified_', ID);
};

const getLoggedInUIVersion = () => {
    return getItem('_loggedIn_UI_version_');
};
const setTwoFlag = (isTwoFA) => {
    return setItem('_twoFactorEnabled_', isTwoFA);
};

const getTwoFlag = () => {
    return getItem('_twoFactorEnabled_');
};

export const AppStorage = {
    getItem,
    setItem,
    removeItem,
    clear,
    // Resolve a logical key to the physical localStorage key name. In secure mode
    // (prod / forced) this is the obfuscated name from KEY_MAP; in dev it's the
    // logical name unchanged. Consumers that must match against a raw `storage`
    // event key (which reports the PHYSICAL name) should use this instead of
    // hardcoding obfuscated literals.
    getPhysicalKey,
    getName,
    isReadOnlyUser,
    getToken,
    getLastLogin,
    getUserRoles,
    loginInfoUpdatedAt,
    getPortalSetting,
    setPortalSetting,
    isLoggedIn: () => !!getName() && !!getToken(),
    performLogin: ({ data }) => {
        setItem('_token_', data.token);
        setItem('_displayName_', data.displayName);
        setItem('_isReadOnlyUser_', data.isReadOnly || false);
        setItem('_lastLogin_', data.lastLogin);
        setItem('_userRoles_', data.roles);
        setItem('_user_sign_', data.defaultSignature || 'absent');
        setItem('_signature_status_', data.signatureStatus);
        setItem('_allowAccess_', JSON.stringify(data.access));
        setItem('_loggedIn_UI_version_', window?._ENV_?.app_version || 'fixed_version_from_storage_service');
        setItem('_twoFactorEnabled_', data.twoFactorEnabled);
        setItem('_login_info_updated_at_', String(Date.now()));
    },
    saveFileUploaderConfig,
    getFileUploaderConfig,
    performLogout: () => {
        const portalSetting = getPortalSetting();
        clear();
        if (portalSetting) {
            setPortalSetting({ ...portalSetting, expired: true });
        }
        setItem('_login_info_updated_at_', Date.now());
    },
    getSignature,
    setSignature,
    getSignatureStatus,
    setSignatureStatus,
    setSignNotifiedStatus,
    getSignNotifiedStatus,
    getAllowAcces,
    getLoggedInUIVersion,
    setTwoFlag,
    getTwoFlag
};
